import React, { ChangeEvent, Fragment, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/analytics';
import * as Sentry from '@sentry/react';
import Spin from 'antd/lib/spin';
import { LoadingIcon } from './components/Spinner';
import PillLabel from '../components/PillLabel';
import { attemptLogin, setUserProfile } from '../features/auth/auth';
import PrivacyInfoCard from './Login/components/PrivacyInfoCard';
import { authApi } from '../api/auth';
import { osName, osVersion } from 'react-device-detect';
import { setStripeId } from '../features/payment/payment';
import { Dialog, Transition } from '@headlessui/react';
import { TTUserProfile } from '../interfaces/user';
import { useAppDispatch } from '../store';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../components/PhoneInputStyles.css';

type Props = {
  className?: string;
  onSuccess: (guest: TTUserProfile) => void;
  onCancel: () => void;
  title?: string;
};

export const LoginPopUp: React.FC<Props> = ({
  onSuccess,
  onCancel,
  title = 'Start Your Order',
}) => {
  const [open, setOpen] = useState(true);

  const [phoneNumber, setPhoneNumber] = useState<string | undefined>('');
  const [showCodeField, setShowCodeField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [showPrivacyPopUp, setShowPrivacyPopUp] = useState(false);
  const [useEmail, setUseEmail] = useState(false);
  const [email, setEmail] = useState('');

  const dispatch = useAppDispatch();

  async function signInViaPhoneNumber() {
    try {
      const response = await authApi.createPhoneVerificationCode({
        phone_number: phoneNumber ?? '',
        channel: useEmail ? 'email' : 'sms',
        email_address: useEmail ? email : null,
      });
      if (response.data.status == 'pending' || response.data.status == 'sent') {
        setError('');
        setLoading(false);
        setShowCodeField(true);
      }
    } catch (e) {
      setLoading(false);
      setError('Error: \n' + (e.response?.data?.detail || e.message));
      Sentry.captureException(error);
    }
  }

  useEffect(() => {
    return () => {};
  }, []);
  async function confirmCode() {
    try {
      const response = await authApi.verifyPhoneVerificationCode({
        phone_number: phoneNumber ?? '',
        code: code,
        os_type: osName + '_' + osVersion,
        email_address: useEmail ? email : null,
        channel: useEmail ? 'email' : 'sms',
      });
      localStorage.setItem('tabletabToken', response.data.token?.access_token);
      localStorage.setItem('tabletabIsLoggedIn', 'true');
      dispatch(setStripeId(response.data.guest?.stripe_id));

      dispatch(setUserProfile(response.data?.guest));
      // ZendeskAPI('webWidget', 'identify', {
      //   name: phoneNumber ?? '',
      // });
      onSuccess(response?.data?.guest);
    } catch (e) {
      setLoading(false);
      setError('Error: \n' + (e.response?.data?.detail || e.message));
      Sentry.captureException(error);
    }
  }
  return (
    <Transition.Root show={open} as={Fragment} appear>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            setOpen(false);
            onCancel();
          }}
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-surface px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8  sm:p-6">
                <svg
                  className={
                    'text-gray-500 w-5 h-5 md:w-8 md:h-8 top-0 right-0 m-1 fixed'
                  }
                  onClick={onCancel}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h2 className="text-2xl leading-9 font-extrabold text-on-surface mb-2">
                    {title}
                  </h2>
                </div>
                {error !== '' && (
                  <div className="rounded-md bg-error-container p-4 my-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-error"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm leading-5 font-medium text-error">
                          {error}
                        </h3>
                      </div>
                    </div>
                  </div>
                )}
                {!showCodeField && (
                  <Spin spinning={loading} indicator={LoadingIcon}>
                    <form
                      className={'w-full '}
                      onSubmit={async e => {
                        e.preventDefault();
                        setLoading(true);
                        await signInViaPhoneNumber();
                      }}
                    >
                      <div>
                        <div className="flex flex-row justify-between items-center">
                          <label
                            htmlFor="tel"
                            className="block text-sm font-medium leading-5 text-on-surface"
                          >
                            Enter Your{' '}
                            {useEmail ? 'Email Adress' : 'Phone Number'}
                          </label>
                          {!showCodeField && (
                            <div className="justify-center content-center items-center">
                              <div
                                className=" rounded"
                                onClick={event => setShowPrivacyPopUp(true)}
                              >
                                <div className="flex flex-col  h-full items-center justify-center align-middle">
                                  <div className="flex flex-row w-full items-center justify-center">
                                    <div className=" flex flex-row items-center text-sm text-left justify-left content-center">
                                      <PillLabel
                                        color="bg-tertiary-container"
                                        text_color="text-on-tertiary-container"
                                        label={'Why do we ask this?'}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-1 rounded-md shadow-sm">
                          {useEmail ? (
                            <input
                              id="email"
                              type="email"
                              required
                              placeholder="acb@abc.com"
                              value={email}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                e.preventDefault();
                                const value = e.target.value.replace(/\s/g, '');
                                setEmail(value);
                              }}
                              className="appearance-none bg-surface-variant block w-full px-3 py-2 border border-surface-variant rounded-md placeholder-on-surface-variant/60 focus:ring-primary/70 focus:border-primary text-on-surface-variant transition duration-150 ease-in-out text-md sm:leading-5"
                            />
                          ) : (
                            <PhoneInput
                              id="tel"
                              international
                              defaultCountry="US"
                              placeholder="Enter phone number"
                              value={phoneNumber}
                              onChange={setPhoneNumber}
                              className="appearance-none bg-surface-variant block w-full px-3 py-2 border border-surface-variant rounded-md placeholder-on-surface-variant/60 focus:ring-primary/70 focus:border-primary text-on-surface-variant transition duration-150 ease-in-out text-md sm:leading-5"
                              style={{
                                '--PhoneInputCountryFlag-height': '1em',
                                '--PhoneInputCountrySelectArrow-color': 'currentColor',
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="mt-6 justify-center content-center items-center">
                        <div className="pr-4 min-w-4/5 sm:h-10 max-h-10 sm:flex rounded">
                          <div className="flex flex-col bg-surface h-full items-center justify-center align-middle">
                            <div className="flex flex-row w-full items-center justify-center">
                              <div className="text-on-surface flex flex-row items-center text-xs text-left justify-left content-center">
                                <p>
                                  {`You will receive an ${
                                    useEmail ? 'email' : 'SMS'
                                  } with a code for verification. Please make sure you have cellular service or WiFi connectivity.`}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6">
                        <button
                          id={'phone-number-submit'}
                          type="submit"
                          className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-on-primary  sm:text-sm"
                        >
                          Continue
                        </button>
                      </div>
                    </form>
                  </Spin>
                )}
                {showCodeField && (
                  <Spin spinning={loading} indicator={LoadingIcon}>
                    <form
                      className={'w-full'}
                      onSubmit={async e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLoading(true);
                        await confirmCode();
                      }}
                    >
                      <div>
                        <label
                          htmlFor="code"
                          className="block text-sm font-medium leading-5 text-on-surface"
                        >
                          Enter Verification Code
                        </label>
                        <div className="mt-1 rounded-md shadow-sm">
                          <input
                            id="code"
                            type="ont-time-code"
                            autoComplete="one-time-code"
                            required
                            value={code}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              e.preventDefault();
                              const value = e.target.value.replace(/\s/g, '');
                              setCode(value);
                            }}
                            className="appearance-none bg-surface-variant block w-full px-3 py-2 border border-surface-variant rounded-md placeholder-on-surface-variant/60 focus:ring-primary/70 focus:border-primary text-on-surface-variant transition duration-150 ease-in-out text-md sm:leading-5"
                          />
                        </div>
                      </div>
                      <div className="mt-6 justify-center content-center items-center">
                        <div className="pr-4 min-w-4/5 sm:h-10 max-h-10 sm:flex rounded">
                          <div className="flex flex-col bg-surface h-full items-center justify-center align-middle">
                            <div className="flex flex-row w-full items-center justify-center">
                              <div className="text-on-surface flex flex-row items-center text-xs text-left justify-left content-center">
                                <p>
                                  {`We have sent a code to ${
                                    useEmail ? email : phoneNumber
                                  }. Press retry if you haven't received it in 60 secs. ${
                                    useEmail
                                      ? 'Please check the updates or spam folders in your email'
                                      : ''
                                  }`}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/*<div id={'recaptcha'} ref={ref => (recaptcha = ref)} />*/}
                      <div className="mt-5 sm:mt-6">
                        <button
                          type="submit"
                          className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-on-primary  sm:text-sm"
                        >
                          Confirm
                        </button>
                      </div>
                      <div className="justify-center content-center items-center mt-6">
                        <div
                          className=" w-full  bg-secondary-container  rounded"
                          onClick={async () => {
                            setLoading(false);
                            setShowCodeField(false);
                            setUseEmail(!useEmail);
                            // await signInViaPhoneNumber();
                          }}
                        >
                          <div className="flex flex-col p-2 rounded-md h-full items-center justify-center align-middle">
                            <div className="flex flex-row w-full items-center justify-center">
                              <div className="text-on-secondary-container flex flex-row items-center text-sm text-left justify-left content-center">
                                Use {useEmail ? 'a phone number' : 'email'}{' '}
                                instead
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="justify-center content-center items-center mt-6">
                        <div
                          className=" w-full  bg-secondary-container  rounded"
                          onClick={async () => {
                            setLoading(false);
                            setShowCodeField(false);
                            // await signInViaPhoneNumber();
                          }}
                        >
                          <div className="flex flex-col p-2 rounded-md h-full items-center justify-center align-middle">
                            <div className="flex flex-row w-full items-center justify-center">
                              <div className="text-on-secondary-container flex flex-row items-center text-sm text-left justify-left content-center">
                                Retry
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>
                  </Spin>
                )}

                {showPrivacyPopUp && (
                  <PrivacyInfoCard
                    callback={() => setShowPrivacyPopUp(false)}
                  />
                )}
                {
                  <div className="justify-center content-center items-center mt-6">
                    <div className="  w-full  rounded">
                      <div className="flex flex-col  bg-surface h-full items-center justify-center align-middle">
                        <span className="py-4 text-on-surface  text-xs text-left">
                          By continuing, you agree to our{' '}
                          <a
                            href="https://tabletab.io/terms"
                            target={'_blank'}
                            className=" text-tertiary"
                          >
                            Terms of Service
                          </a>{' '}
                          &{' '}
                          <a
                            href={'https://tabletab.io/privacy'}
                            target={'_blank'}
                            className=" text-tertiary"
                          >
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </div>
                    </div>
                  </div>
                }
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
